import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { Lock, CheckCircle, Circle, XCircle, Clock } from 'lucide-react'
import { completeTimedObjective } from '../lib/fragoEngine'

// Phase 5 of the FRAGO Rework — shared phase-progression view.
//
// Renders phases + objectives for one FRAGO with three visual states:
//   locked     → 🔒 (greyed; downstream of an incomplete gate)
//   active     → 🔵 (highlighted; do these now)
//   completed  → ✓ (muted green)
//   failed     → ✕ (red)
//
// Variants:
//   compact   — single-line phase strip, no objectives. Used in
//               GameEvent SITREP and FragoCard summaries.
//   default   — full stepper with per-phase briefing + objective
//               checklist. Used in FragoDetail and the player
//               MISSIONS tab.
//
// faction-scoped views (player MISSIONS tab) should pass
// `viewerNodeIds` so phases delegated to nodes the viewer isn't on get
// shown as locked even if their gate is open — this matches the spec's
// "only show the assignments the player owns" rule.

const S = {
  display: { fontFamily: "'VT323', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
  muted: { color: '#5a6470' },
  active: { color: '#ffcc33' },
  done:   { color: '#9aff9a' },
  fail:   { color: '#ff2a2a' },
  panel: { background: 'rgba(0, 20, 8, .4)', border: '1px solid rgba(0,255,102,.18)' },
}

const phaseIcon = (status) => {
  if (status === 'completed') return <CheckCircle size={14} color="#9aff9a" />
  if (status === 'active')    return <Circle size={14} color="#ffcc33" />
  if (status === 'failed')    return <XCircle size={14} color="#ff2a2a" />
  return <Lock size={14} color="#5a6470" />
}
const phaseColor = (status) =>
  status === 'completed' ? '#9aff9a'
  : status === 'active' ? '#ffcc33'
  : status === 'failed' ? '#ff2a2a'
  : '#5a6470'

// Same lookup table the engine uses; mirrored here so renderers can
// surface a human-readable label without a round-trip.
const OBJECTIVE_LABEL = {
  equipment_scan: 'Scan equipment',
  checkpoint_scan: 'Capture checkpoint',
  player_scan: 'Make contact with player',
  kill_target: 'Eliminate target',
  timed_hold: 'Hold position',
  defend_player: 'Defend player',
  escort_player: 'Escort player',
  manual: 'GM resolution',
}

// FRAGO restructure — fog of war scope.
//   'full'   (default) — every phase + objective at every status.
//                        Used in the GM command center, FragoDetail
//                        for GMs / platform staff, etc.
//   'player' — only active phases (with active + completed
//              objectives) and completed/failed phases. Locked
//              phases AND locked objectives within active phases
//              are hidden entirely — no "Phase 2 (LOCKED)" hint
//              that would let the player pre-position.
export default function FragoPhaseStepper({ fragoId, variant = 'default', viewerScope = 'full' }) {
  const [phases, setPhases] = useState([])
  const [objectives, setObjectives] = useState([])
  const [loading, setLoading] = useState(true)
  // Timer engine — re-render every second so any active timed objective
  // can display a live countdown calculated against its phase's
  // activated_at. When the countdown elapses, the relevant render
  // branch fires completeTimedObjective() and refetches.
  const [, setTick] = useState(0)

  const reload = async () => {
    const { data: phaseRows } = await supabase
      .from('frago_phases')
      .select('*')
      .eq('frago_id', fragoId)
      .order('phase_number')
    const phaseIds = (phaseRows || []).map(p => p.id)
    let objRows = []
    if (phaseIds.length > 0) {
      const { data } = await supabase
        .from('frago_objectives')
        .select('*')
        .in('phase_id', phaseIds)
        .order('objective_number')
      objRows = data || []
    }
    setPhases(phaseRows || [])
    setObjectives(objRows)
  }

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      await reload()
      if (cancelled) return
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  // reload depends on fragoId via the supabase query; the lint
  // exhaustive-deps warning is intentional — we don't want reload as
  // a dependency because the function identity changes every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fragoId])

  // Timer engine — 1s tick when any objective is timed + active. Drives
  // the countdown render and fires the completion RPC when the timer
  // elapses. Cleans up when the component unmounts or the objectives
  // change to all-non-timed.
  useEffect(() => {
    const hasActiveTimer = objectives.some(o =>
      o.status === 'active' &&
      (o.objective_type === 'timed_hold' || o.objective_type === 'defend_player') &&
      o.duration_minutes
    )
    if (!hasActiveTimer) return
    const interval = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(interval)
  }, [objectives])

  // Fire completion when a timed objective's countdown crosses zero.
  // Checked on every render (cheap) so we don't need a separate state
  // machine — completeTimedObjective is idempotent on the server side
  // (not_active branch returns ok:false without side effects), so a
  // double-fire from React's strict-mode double-invoke is harmless.
  useEffect(() => {
    const now = Date.now()
    objectives.forEach(o => {
      if (o.status !== 'active') return
      if (o.objective_type !== 'timed_hold' && o.objective_type !== 'defend_player') return
      if (!o.duration_minutes) return
      const phase = phases.find(p => p.id === o.phase_id)
      if (!phase?.activated_at) return
      const endsAt = new Date(phase.activated_at).getTime() + o.duration_minutes * 60 * 1000
      if (now >= endsAt) {
        // Fire and forget — reload picks up the new state.
        completeTimedObjective(o.id).then((r) => {
          if (r?.ok) reload()
        }).catch(() => {})
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectives, phases])

  if (loading) {
    return <p style={{ ...S.mono, fontSize: 11, color: '#5a6470' }}>Loading mission structure…</p>
  }
  if (phases.length === 0) {
    return <p style={{ ...S.mono, fontSize: 11, color: '#5a6470' }}>No phases configured for this FRAGO yet.</p>
  }

  // Fog-of-war filter. Player view drops locked phases entirely so
  // future steps remain a surprise. GM / co-GM / full-scope view
  // keeps everything.
  const isPlayerScope = viewerScope === 'player'
  const visiblePhases = isPlayerScope
    ? phases.filter(p => p.status !== 'locked')
    : phases
  const isObjectiveVisible = (obj) => {
    if (!isPlayerScope) return true
    // Within an active phase, hide objectives that haven't started yet.
    // Completed / active / failed all render so the player can see
    // what's done and what's next.
    return obj.status !== 'locked'
  }

  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {visiblePhases.map((p) => {
          // Preserve the original phase number even when earlier
          // phases are hidden under fog-of-war — players still know
          // they're on "Phase 2" if they completed a prior phase.
          const realIdx = phases.findIndex(x => x.id === p.id)
          return (
            <span key={p.id} className="flex items-center gap-1" style={{
              ...S.mono, fontSize: 10, color: phaseColor(p.status),
              padding: '2px 6px',
              background: phaseColor(p.status) + '14',
              border: `1px solid ${phaseColor(p.status)}66`,
              letterSpacing: '0.08em', textTransform: 'uppercase',
            }}>
              {phaseIcon(p.status)} P{realIdx + 1}
            </span>
          )
        })}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {visiblePhases.map((phase) => {
        const idx = phases.findIndex(p => p.id === phase.id)
        const phaseObjs = objectives.filter(o => o.phase_id === phase.id && isObjectiveVisible(o))
        const color = phaseColor(phase.status)
        return (
          <div key={phase.id} className="p-3" style={{ ...S.panel, border: `1px solid ${color}55` }}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                {phaseIcon(phase.status)}
                <span style={{ ...S.display, fontSize: 13, color, letterSpacing: '0.1em' }}>
                  PHASE {idx + 1}: {phase.title}
                </span>
              </div>
              <span style={{ ...S.mono, fontSize: 9, color, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                {phase.status}
              </span>
            </div>
            {phase.briefing && (
              <p style={{ ...S.mono, fontSize: 11, color: '#9aa6b0', marginBottom: 6, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
                {phase.briefing}
              </p>
            )}
            <p style={{ ...S.mono, fontSize: 9, color: '#5a6470', marginBottom: 6, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Assignment: {phase.assignment_type}
              {phase.status === 'locked' && idx > 0 && (
                <span style={{ marginLeft: 8 }}>· Complete Phase {idx} first</span>
              )}
            </p>
            <div className="space-y-1">
              {phaseObjs.length === 0 ? (
                <p style={{ ...S.mono, fontSize: 10, color: '#5a6470' }}>No objectives.</p>
              ) : phaseObjs.map((obj) => {
                const objColor = phaseColor(obj.status)
                const Icon =
                  obj.status === 'completed' ? CheckCircle :
                  obj.status === 'failed'    ? XCircle :
                  obj.status === 'active'    ? Circle :
                  Lock
                // Timer engine — for an active timed_hold / defend_player
                // objective, compute remaining time from the phase's
                // activated_at. The 1s tick effect above re-renders the
                // stepper so this display updates live.
                const isTimed = obj.status === 'active'
                  && (obj.objective_type === 'timed_hold' || obj.objective_type === 'defend_player')
                  && obj.duration_minutes
                  && phase.activated_at
                let countdownLabel = null
                if (isTimed) {
                  const endsAt = new Date(phase.activated_at).getTime() + obj.duration_minutes * 60 * 1000
                  const remainingMs = Math.max(0, endsAt - Date.now())
                  const mins = Math.floor(remainingMs / 60000)
                  const secs = Math.floor((remainingMs % 60000) / 1000)
                  countdownLabel = `${mins}:${String(secs).padStart(2, '0')}`
                }
                return (
                  <div key={obj.id} className="flex items-center gap-2 flex-wrap" style={{ fontSize: 11 }}>
                    <Icon size={12} color={objColor} />
                    <span style={{ ...S.mono, color: objColor }}>
                      {obj.title}
                    </span>
                    <span style={{ ...S.mono, fontSize: 9, color: '#5a6470', marginLeft: 4 }}>
                      ({OBJECTIVE_LABEL[obj.objective_type] || obj.objective_type})
                    </span>
                    {countdownLabel ? (
                      <span className="flex items-center gap-1" style={{
                        ...S.mono, fontSize: 10, color: '#4dd6ff', marginLeft: 4,
                        fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                      }}>
                        <Clock size={10} /> {countdownLabel}
                      </span>
                    ) : obj.duration_minutes ? (
                      <span className="flex items-center gap-1" style={{ ...S.mono, fontSize: 9, color: '#4dd6ff', marginLeft: 4 }}>
                        <Clock size={9} /> {obj.duration_minutes}m
                      </span>
                    ) : null}
                    {obj.concurrent && (
                      <span style={{ ...S.mono, fontSize: 8, color: '#4da0ff', letterSpacing: '0.1em' }}>
                        · CONCURRENT
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
